import React, { useCallback, useEffect, useState } from "react";

import { Box, Card, Collapse, IconButton, Tooltip, Typography } from "@mui/material";

import { alpha, useTheme } from "@mui/material/styles";
import {
  ArrowClockwiseIcon,
  PaperPlaneTiltIcon,
  PencilSimpleLineIcon,
  TrashIcon,
} from "@phosphor-icons/react/dist/ssr";

import useLesson from "@/contexts/useLesson";

import ListCardHeader from "./ListCardHeader";

interface Props {
  index: number;
  id: string;
  children: React.ReactNode;
  isApproved: boolean;
  onApprove?: (index: number) => void;
  onRegenerate?: (index: number) => void;
  title: string;
  onDelete?: (index: number) => void;
  onEdit?: (index: number) => void;
  onOpenModal?: () => void;
  isGenerated?: boolean;
  status?: string;
  isRead?: boolean;
  isFocused?: boolean;
  appearance?: "default" | "example";
  viewMoreLabel?: string;
}

const ListCard: React.FC<Props> = ({
  index,
  id,
  isApproved,
  onApprove,
  onRegenerate,
  children,
  title,
  onDelete,
  onEdit,
  onOpenModal,
  isGenerated,
  status,
  isRead,
  isFocused,
  appearance = "default",
  viewMoreLabel,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const { lessonResourceState, setLessonResourceState } = useLesson();
  const theme = useTheme();
  const isExampleAppearance = appearance === "example";

  const handleRead = useCallback(() => {
    if (!lessonResourceState) return;
    const readItems = lessonResourceState.readItems || [];
    const newReadItems = [...readItems, id];
    setLessonResourceState({
      ...lessonResourceState,
      readItems: newReadItems,
    });
  }, [id, lessonResourceState, setLessonResourceState]);

  useEffect(() => {
    if (!isRead && isOpen && isFocused) handleRead();
  }, [handleRead, isRead, isOpen, isFocused]);

  return (
    <Card
      sx={{
        width: "100%",
        flexDirection: "column",
        alignItems: "flex-start",
        p: 0,
        mb: 2,
        border: "none",
        borderRadius: isExampleAppearance ? 0 : 2,
        boxShadow: "none",
        backgroundColor: isExampleAppearance ? "transparent" : "background.default",
        overflow: isExampleAppearance ? "visible" : "hidden",
      }}
    >
      <ListCardHeader
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={title}
        isApproved={isApproved}
        onOpenModal={() => {
          handleRead();
          onOpenModal?.();
        }}
        status={status}
        isRead={isRead}
        appearance={appearance}
      />
      <Collapse in={isOpen} sx={{ width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            maxHeight: isExampleAppearance ? "140px" : "120px",
            overflow: "hidden",
            background: isExampleAppearance ? "transparent" : "background.default",
          }}
        >
          <Box
            sx={{
              pt: isExampleAppearance ? 1.5 : 2,
              pb: isExampleAppearance ? 3 : 3,
              px: isExampleAppearance ? 1.5 : 2,
            }}
          >
            {children}
          </Box>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "60px",
              background: `linear-gradient(to bottom, 
                ${alpha(isExampleAppearance ? theme.palette.background.default : theme.palette.background.default, 0)} 0%, 
                ${alpha(isExampleAppearance ? theme.palette.background.default : theme.palette.background.default, 0.9)} 60%, 
                ${isExampleAppearance ? theme.palette.background.default : theme.palette.background.default} 100%)`,
              pointerEvents: "none",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              pb: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "primary.main",
                userSelect: "none",
              }}
            >
              {status === "incomplete" ? "Solve Problem" : (viewMoreLabel || "View more")}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            py: isExampleAppearance ? 0.5 : 1,
            px: isExampleAppearance ? 1.5 : 1,
            justifyContent: "flex-end",
            width: "100%",
            borderTop: "none",
            backgroundColor: isExampleAppearance ? "transparent" : "background.default",
          }}
        >
          <>
            {!isApproved && onApprove && (
              isExampleAppearance ? (
                <Tooltip title="Send to Student" arrow>
                  <IconButton
                    onClick={() => onApprove?.(index)}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: "primary.main",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <PaperPlaneTiltIcon size={16} weight="bold" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Send to Student" arrow>
                  <Box
                    onClick={() => onApprove?.(index)}
                    className="bg-blue-500 hover:bg-blue-400 cursor-pointer rounded-md p-3 flex items-center justify-center"
                  >
                    <PaperPlaneTiltIcon
                      size={16}
                      weight="bold"
                      style={{ color: theme.palette.common.white }}
                    />
                  </Box>
                </Tooltip>
              )
            )}
            {!isApproved && onEdit && (
              isExampleAppearance ? (
                <Tooltip title="Edit" arrow>
                  <IconButton
                    onClick={() => onEdit?.(index)}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: "text.primary",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                      },
                    }}
                  >
                    <PencilSimpleLineIcon size={16} weight="bold" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Edit" arrow>
                  <Box
                    onClick={() => onEdit?.(index)}
                    className="bg-indigo-500 hover:bg-indigo-400 cursor-pointer rounded-md p-3 flex items-center justify-center"
                  >
                    <PencilSimpleLineIcon
                      size={16}
                      weight="bold"
                      style={{ color: theme.palette.common.white }}
                    />
                  </Box>
                </Tooltip>
              )
            )}
            {!isApproved && isGenerated && onRegenerate && (
              isExampleAppearance ? (
                <Tooltip title="Regenerate" arrow>
                  <IconButton
                    onClick={() => onRegenerate?.(index)}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: "text.primary",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                      },
                    }}
                  >
                    <ArrowClockwiseIcon size={16} weight="bold" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Regenerate" arrow>
                  <Box
                    onClick={() => onRegenerate?.(index)}
                    className="bg-amber-500 hover:bg-amber-400 cursor-pointer rounded-md p-3 flex items-center justify-center"
                  >
                    <ArrowClockwiseIcon
                      size={16}
                      weight="bold"
                      style={{ color: theme.palette.common.white }}
                    />
                  </Box>
                </Tooltip>
              )
            )}
            {!isApproved && onDelete && (
              isExampleAppearance ? (
                <Tooltip title="Delete" arrow>
                  <IconButton
                    onClick={() => onDelete?.(index)}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: "error.main",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      },
                    }}
                  >
                    <TrashIcon size={16} weight="bold" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Delete" arrow>
                  <Box
                    onClick={() => onDelete?.(index)}
                    className="bg-red-500 hover:bg-red-400 cursor-pointer rounded-md p-3 flex items-center justify-center"
                  >
                    <TrashIcon
                      size={16}
                      weight="bold"
                      style={{ color: theme.palette.common.white }}
                    />
                  </Box>
                </Tooltip>
              )
            )}
          </>
        </Box>
      </Collapse>
    </Card>
  );
};

export default ListCard;
